
import React, { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Store } from "@/types";
import { assignUserToStore, getStoresByUser, removeUserFromStore } from "@/services/store";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

interface StoreAssignmentProps {
  userId: string;
  stores: Store[];
  onUpdate?: () => void;
}

const StoreAssignment: React.FC<StoreAssignmentProps> = ({ userId, stores, onUpdate }) => {
  const [userStores, setUserStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchUserStores();
    }
  }, [userId]);
  
  const fetchUserStores = async () => {
    setLoading(true);
    try {
      const assignedStores = await getStoresByUser(userId);
      // Filtrar as lojas para obter apenas as que estão associadas ao utilizador
      const userStoresList = stores.filter(store => 
        assignedStores.some(assignedStore => assignedStore.id === store.id)
      );
      setUserStores(userStoresList);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar as lojas associadas ao utilizador",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignStore = async () => {
    if (!selectedStoreId) return;
    
    setAdding(true);
    try {
      const result = await assignUserToStore(userId, selectedStoreId);
      if (result.success) {
        fetchUserStores();
        setSelectedStoreId("");
        if (onUpdate) onUpdate();
      } else {
        toast({
          title: "Erro",
          description: result.error || "Não foi possível associar a loja ao utilizador",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível associar a loja ao utilizador",
        variant: "destructive",
      });
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveStore = async (storeId: string) => {
    try {
      const result = await removeUserFromStore(userId, storeId);
      if (result.success) {
        fetchUserStores();
        if (onUpdate) onUpdate();
      } else {
        toast({
          title: "Erro",
          description: result.error || "Não foi possível remover a loja do utilizador",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível remover a loja do utilizador",
        variant: "destructive",
      });
    }
  };

  // Filtrar lojas disponíveis (que ainda não foram associadas ao utilizador)
  const availableStores = stores.filter(
    store => !userStores.some(userStore => userStore.id === store.id)
  );

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium">Lojas associadas</h4>
      
      {loading ? (
        <div className="flex justify-center py-2">
          <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-brand-red"></div>
        </div>
      ) : userStores.length === 0 ? (
        <p className="text-sm text-gray-500">Nenhuma loja associada</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {userStores.map(store => (
            <Badge key={store.id} variant="outline" className="flex items-center gap-1 bg-gray-800">
              {store.name}
              <button 
                onClick={() => handleRemoveStore(store.id)}
                className="ml-1 rounded-full p-0.5 hover:bg-gray-700"
              >
                <X size={12} />
              </button>
            </Badge>
          ))}
        </div>
      )}
      
      <div className="flex gap-2">
        <Select value={selectedStoreId} onValueChange={setSelectedStoreId} disabled={availableStores.length === 0}>
          <SelectTrigger className="bg-gray-800 border-gray-700">
            <SelectValue placeholder="Selecionar loja" />
          </SelectTrigger>
          <SelectContent className="bg-brand-dark border-gray-700">
            {availableStores.map(store => (
              <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button 
          onClick={handleAssignStore}
          disabled={!selectedStoreId || adding}
          className="bg-brand-red hover:bg-brand-red/90"
          size="sm"
        >
          {adding ? (
            <>
              <span className="h-4 w-4 mr-2 animate-spin rounded-full border-b-transparent border-2"></span>
              Adicionando...
            </>
          ) : (
            "Adicionar"
          )}
        </Button>
      </div>
    </div>
  );
};

export default StoreAssignment;
